import { useMutation } from '@platform/api-client';
import { useToast } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { bodyFromForm, initialStockLines } from '../product-body.ts';
import type { ProductFormValues } from '../product-form.ts';
import type { Product, ProductStatus } from '../product.ts';
import { useProductStatusActions } from '../useProductStatus.ts';
import { queryKeys, usePageContext } from '../../usePageContext.ts';

export type SaveOutcome = 'created' | 'saved' | 'published' | 'deactivated';

export type StockPolicy = {
  readonly canWrite: boolean;
  readonly acceptsInitialStock: (variantId: string) => boolean;
};

type ActionsInput = {
  readonly api: TypedClient;
  readonly product: Product | null;
  readonly canSeeCost: boolean;
  readonly stock: StockPolicy;
  readonly onSaved: (saved: Product, outcome: SaveOutcome) => void;
  readonly onArchived: () => void;
};

export const useProductActions = ({ api, product, canSeeCost, stock, onSaved, onArchived }: ActionsInput) => {
  const { t, tenantId, notify, queryClient, invalidate, describeError } = usePageContext();
  const toast = useToast();
  const statusActions = useProductStatusActions(api);

  const remember = (saved: Product) => {
    queryClient.setQueryData(queryKeys.product(tenantId, saved.id), saved);
    void invalidate('products', 'dashboard', 'stock-levels');
  };

  const warn = (error: unknown, context?: string) => {
    const reason = describeError(error) ?? t('errors.UNKNOWN');
    toast.notify({ tone: 'danger', message: context === undefined ? reason : `${context} ${reason}` });
  };

  const writeProduct = (values: ProductFormValues, status: ProductStatus, current: Product | null) => {
    const body = bodyFromForm(values, { product: current, canSeeCost });
    return current === null
      ? api.post('/admin/products', { body })
      : api.patch('/admin/products/{id}', {
          path: { id: current.id },
          body: { ...body, version: current.version, status },
        });
  };

  const recordInitialStock = async (values: ProductFormValues, saved: Product) => {
    const lines = stock.canWrite ? initialStockLines(values, saved, stock.acceptsInitialStock) : [];
    if (lines.length === 0) return;
    try {
      await api.post('/admin/inventory/initial-stock', { body: { lines } });
      void invalidate('stock-levels', 'low-stock', 'movements');
    } catch (error) {
      warn(error, t('productEditor.initialStockFailed'));
    }
  };

  const save = useMutation({
    mutationFn: async ({ values, status }: { values: ProductFormValues; status: ProductStatus }) => {
      const saved = await writeProduct(values, status, product);
      remember(saved);
      await recordInitialStock(values, saved);
      return saved;
    },
  });

  const archive = useMutation({
    mutationFn: (current: Product) => statusActions.archive([current]),
  });

  return {
    pending: save.isPending || archive.isPending,
    error: save.error ?? archive.error,
    autosave: async (values: ProductFormValues): Promise<Product> => {
      const saved = await writeProduct(values, 'draft', product);
      remember(saved);
      return saved;
    },
    save: (values: ProductFormValues, status: ProductStatus, outcome: SaveOutcome) => {
      save.mutate(
        { values, status },
        {
          onSuccess: (saved) => {
            notify(t(`productEditor.outcome_${outcome}`));
            onSaved(saved, outcome);
          },
        },
      );
    },
    archive: () => {
      if (product === null) return;
      archive.mutate(product, {
        onSuccess: ({ changed: [archived] }) => {
          if (archived === undefined) return;
          remember(archived);
          onArchived();
        },
      });
    },
  };
};
